import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useEffect, useState } from "react";
import axios, { AxiosResponse } from "axios";
import AceEditor from "react-ace";
import ScrollDialog from "../PopUpPage/PopUpPage";
import AsyncApiComponent from "@asyncapi/react-component";
import "@asyncapi/react-component/styles/default.css";
import { makeStyles } from "@mui/styles";
import generateAccessToken from "../../services/generateAccessToken";
import { Button, CircularProgress } from "@mui/material";
import config from "../../config";
import iamSpec from '../../specs/iam.json';
import connectSpec from '../../specs/connect.json';
import fileServiceStatusSpec from '../../specs/fileservice-status.json';
import "ace-builds/src-noconflict/mode-yaml";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        padding: "10px 20px",
        backgroundColor: "#00437B",
        color: "#FFFFFF",
        fontWeight: "bold",
        fontSize: "16px",
        border: 0,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
        backgroundColor: "#CFE1EE",
    },
    "&:nth-of-type(even)": {
        backgroundColor: "#FFFFFF",
    },
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const useStyles = makeStyles({
    tableContainer: {
        minWidth: "1000px",
        minHeight: "400px",
    },
    circularProgress: {
        position: "relative",
        left: "450px",
        top: "40px",
    },
});

interface IEvent {
    doc_filename: string;
    event_name: string;
    event_version: string;
    async_api_spec?: any;
    stg_async_api_spec?: any;
    event_spec?: any;
}

const EventsTable: React.FC = (props) => {
    const [eventsList, setEventsList] = useState<IEvent[]>();
    const [dataLoaded, setDataLoaded] = useState(false);
    const styles = useStyles();

    const getEventDetails = async () => {
        if (sessionStorage.getItem("access_token") === null) {
            await generateAccessToken();
        } else {
            axios
                .get(`${config.EVENTS_ENDPOINT}/events`, {
                    headers: {
                        Authorization: `Bearer ${sessionStorage.getItem(
                            "access_token"
                        )}`,
                    },
                })
                .then((response: AxiosResponse) => {
                    setEventsList(response.data);
                    setDataLoaded(true);
                });
        }
    };

    useEffect(() => {
        getEventDetails();
    }, []);

    // const formattedSpec = (spec: string) =>{
    //     // const parsed = JSON.parse(spec);
    //     // return JSON.stringify(parsed,null,2);
    //     return spec;
    // }

    const getEllipsisDescription = (event: IEvent) => {
        let description = event?.async_api_spec?.info?.description;
        if (!description) {
            description = event?.stg_async_api_spec?.info?.description;
        }
        return description && description?.length > 200
            ? description.slice(0, 200) + "..."
            : description;
    };
    const getEventSpecInJson = (event: IEvent) => {
        if (event?.event_name === 'IAM') {
            return iamSpec;
        }
        if (event?.event_name === 'File Service Events') {
            return fileServiceStatusSpec;
        }
        if (event?.event_name === 'Connect') {
            return connectSpec;
        }
    
        let jsonSpec = event?.event_spec;
        if (!jsonSpec || Object.keys(jsonSpec).length === 0) {
            jsonSpec = event?.async_api_spec;
        }
    
        return jsonSpec;
    };

    return (
        <div>
            <TableContainer className={styles.tableContainer}>
                <Table sx={{ maxWidth: 1000 }} aria-label="customized table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell
                                style={{ width: "15%" }}
                                rowSpan={2}
                            >
                                Event Name
                            </StyledTableCell>
                            <StyledTableCell
                                style={{ width: "40%" }}
                                align="center"
                                rowSpan={2}
                            >
                                Description
                            </StyledTableCell>
                            <StyledTableCell
                                style={{ width: "10%" }}
                                align="center"
                                rowSpan={2}
                            >
                                Introductory Documentation
                            </StyledTableCell>
                            <StyledTableCell
                                style={{ width: "5%" }}
                                align="center"
                                rowSpan={2}
                            >
                                Version
                            </StyledTableCell>
                            <StyledTableCell
                                style={{ width: "30%" }}
                                align="center"
                                colSpan={2}
                                rowSpan={1}
                            >
                                Specification
                            </StyledTableCell>
                        </TableRow>
                        <TableRow>
                            <StyledTableCell
                                style={{ width: "15%" }}
                                align="center"
                                colSpan={1}
                                rowSpan={1}
                            >
                                <div style={{ whiteSpace: "nowrap" }}>
                                    Available <br />
                                    only in <br />
                                    Pre-Production
                                </div>
                            </StyledTableCell>
                            <StyledTableCell
                                style={{ width: "15%" }}
                                align="center"
                                colSpan={1}
                                rowSpan={1}
                            >
                                Production
                            </StyledTableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {!dataLoaded ? (
                            <CircularProgress
                                className={styles.circularProgress}
                            />
                        ) : (
                            eventsList !== undefined &&
                            eventsList.map((event) => (
                                <StyledTableRow key={event.event_name}>
                                    <StyledTableCell
                                        component="th"
                                        scope="row"
                                        style={{
                                            fontWeight: 500,
                                            fontSize: "16px",
                                        }}
                                    >
                                        {event.event_name}
                                    </StyledTableCell>
                                    <StyledTableCell component="th" scope="row">
                                        {getEllipsisDescription(event)}
                                    </StyledTableCell>
                                    <StyledTableCell
                                        align="center"
                                        component="th"
                                        scope="row"
                                    >
                                        <Button
                                            target="_blank"
                                            href={`https://events-catalog.stage.events.trimblecloud.com/${event?.doc_filename}/introduction.html`}
                                        >
                                            Click Here
                                        </Button>
                                    </StyledTableCell>

                                    <StyledTableCell align="center">
                                        {event?.event_version}
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        {event?.stg_async_api_spec && (
                                            <ScrollDialog
                                                title="Schema"
                                                buttonContent="View JSON"
                                                dialogWidth="lg"
                                                scrollType="body"
                                            >
                                                <AceEditor
                                                    style={{
                                                        height: "70vh",
                                                        width: "100%",
                                                        overflow: "hidden",
                                                    }}
                                                    placeholder="Start Coding"
                                                    mode="yaml"
                                                    // theme="monokai"
                                                    name="basic-code-editor"
                                                    fontSize={"16px"}
                                                    showGutter={true}
                                                    highlightActiveLine={true}
                                                    value={JSON.stringify(
                                                        event?.stg_async_api_spec,
                                                        null,
                                                        2
                                                    )}
                                                    wrapEnabled={true}
                                                    setOptions={{
                                                        readOnly: true,
                                                        enableBasicAutocompletion:
                                                            true,
                                                        enableLiveAutocompletion:
                                                            true,
                                                        enableSnippets: true,
                                                        showLineNumbers: true,
                                                        tabSize: 2,
                                                    }}
                                                />
                                            </ScrollDialog>
                                        )}
                                        {event?.stg_async_api_spec && (
                                            <ScrollDialog
                                                title="HTML DOC"
                                                buttonContent="HTML DOC"
                                                dialogWidth="lg"
                                                scrollType="paper"
                                            >
                                                <AsyncApiComponent
                                                    schema={
                                                        event.stg_async_api_spec
                                                    }
                                                ></AsyncApiComponent>
                                            </ScrollDialog>
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        { getEventSpecInJson(event) && (
                                            <ScrollDialog
                                                title="Schema"
                                                buttonContent="View JSON"
                                                dialogWidth="lg"
                                                scrollType="body"
                                            >
                                                <AceEditor
                                                    style={{
                                                        height: "70vh",
                                                        width: "100%",
                                                        overflow: "hidden",
                                                    }}
                                                    placeholder="Start Coding"
                                                    mode="yaml"
                                                    // theme="monokai"
                                                    name="basic-code-editor"
                                                    fontSize={"16px"}
                                                    showGutter={true}
                                                    highlightActiveLine={true}
                                                    value={JSON.stringify(
                                                        getEventSpecInJson(
                                                            event
                                                        ),
                                                        null,
                                                        2
                                                    )}
                                                    wrapEnabled={true}
                                                    setOptions={{
                                                        readOnly: true,
                                                        enableBasicAutocompletion:
                                                            true,
                                                        enableLiveAutocompletion:
                                                            true,
                                                        enableSnippets: true,
                                                        showLineNumbers: true,
                                                        tabSize: 2,
                                                    }}
                                                />
                                            </ScrollDialog>
                                        )}
                                        {event?.async_api_spec && (
                                            <ScrollDialog
                                                title="HTML DOC"
                                                buttonContent="HTML DOC"
                                                dialogWidth="lg"
                                                scrollType="paper"
                                            >
                                                <AsyncApiComponent
                                                    schema={
                                                        event.async_api_spec
                                                    }
                                                ></AsyncApiComponent>
                                            </ScrollDialog>
                                        )}
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default EventsTable;
